import { takeLatest, put, select, call } from 'redux-saga/effects';
import { Map as ImmutableMap } from 'immutable';
import { v4 as uuidv4 } from 'uuid';
import { DateTime } from 'luxon';
import type { List as ImmutableList } from 'immutable';

import { accountsSelectors } from 'companion-app-components/flux/accounts';
import { isEntityShareUser } from 'companion-app-components/flux/account-groups/accountGroupsSelectors';
import { transactionsActions } from 'companion-app-components/flux/transactions';
import type { CashFlowTransaction } from 'companion-app-components/flux/transactions/transactionsTypes';

import { getTransactionsByAccountId } from 'data/transactions/selectors';
import { getFieldString } from 'data/transactions/searchFilter';
import { isCreateMemorizedRulesEnabled } from 'data/preferences/selectors';

import { mkPayee, Payee } from './types';
import * as actions from './actions';
import { upsertMemorizedPayeeRule } from './utils';

export function buildPayeeList(
  accountsById: ImmutableMap<string, any>,
  transactionsByAccountId: ImmutableMap<string, ImmutableList<CashFlowTransaction>>,
  resources: { accountId: string }[] | null,
) {

  let changedAccounts;
  if (resources) {
    changedAccounts = resources.map((x) => x.accountId);
    changedAccounts = changedAccounts.filter((item, pos) => changedAccounts.indexOf(item) === pos);
  } else {
    changedAccounts = accountsById.map((x) => x.id);
  }

  let payeeListById: ImmutableMap<string, ImmutableMap<string, Payee>> = ImmutableMap();

  // now for each account changed, rebuild the payee list
  changedAccounts.forEach((accountId) => {

    const acct = accountsById.get(accountId);
    if (acct && acct.type !== 'LOAN') {
      let payeeList: ImmutableMap<string, Payee> = ImmutableMap();
      const txns = transactionsByAccountId.get(accountId);
      if (txns) {   
        (txns as any).forEach((txn: CashFlowTransaction) => {
          if (!txn.isDeleted && (txn.payee?.length || 0) > 0) {

            const payeeObject = {
              id: uuidv4(),
              name: txn.payee,
              amountString: getFieldString('amount', txn, undefined),
              catString: getFieldString('category', txn, undefined),
              tagString: getFieldString('tags', txn, undefined),
              txn,
              count: 1,
            };

            const key = txn.payee ? txn.payee?.toLowerCase() : '';
            const prevPayee = payeeList.get(key);
            if (prevPayee) {
              if (DateTime.fromISO(prevPayee.txn?.postedOn || '') < DateTime.fromISO(txn.postedOn)) {
                payeeList = payeeList.set(key, mkPayee({ ...payeeObject, count: payeeObject.count + 1 }));
              }
            } else {
              payeeList = payeeList.set(key, mkPayee(payeeObject));
            }
          }
        });
      }
      if (payeeList.size > 0) {
        payeeListById = payeeListById.set(accountId, payeeList);
      }
    }
  });

  return payeeListById;

}

export function* createPayeeList() {
  // @ts-expect-error - Expected at least 2 arguments, but got 1.ts(2555)
  const transactionsByAccountId = yield select(getTransactionsByAccountId);
  const accountsById = yield select(accountsSelectors.getAccountsById);
  const payeeListById = buildPayeeList(accountsById, transactionsByAccountId, null);

  yield put(actions.updatePayeeListSuccess(payeeListById));

}

export function* updatePayeeList(data) {

  if (data && data.payload && data.payload.resources) {
    // @ts-expect-error - Expected at least 2 arguments, but got 1.ts(2555)
    const transactionsByAccountId = yield select(getTransactionsByAccountId);
    const accountsById = yield select(accountsSelectors.getAccountsById);
    const payeeListById = buildPayeeList(accountsById, transactionsByAccountId, data.payload.resources);

    yield put(actions.updatePayeeListSuccess(payeeListById));

  }
}

export function* triggerPayeeListUpdate(data) {
  let payload = { resources: [data.payload] };
  // @ts-expect-error - Expected 1-2 arguments, but got 0.ts(2554)
  if (data.type === transactionsActions.getTransactionsSuccess().type) {
    payload = data.payload;
    // @ts-expect-error - Expected 1-2 arguments, but got 0.ts(2554)
  } else if (data.type === transactionsActions.batchTransactions().type) {
    payload = { resources: data.payload };
  }

  yield put(actions.updatePayeeListAsync(payload));
}

export function* updatePayeeListActionWatcher() {
  yield takeLatest(actions.updatePayeeList, updatePayeeList);
}
export function* createPayeeListActionWatcher() {
  yield takeLatest(actions.createPayeeList, createPayeeList);
}

export function* transactionChangeActionWatcher() {
  yield takeLatest([
    transactionsActions.batchTransactions,
    transactionsActions.createTransaction,
    transactionsActions.updateTransaction,
    transactionsActions.deleteTransaction,
    transactionsActions.getTransactionsSuccess,
  ], triggerPayeeListUpdate);
}

function* updateMemorizedPayee(action) {
  const state = yield select(); 
  const isEnabled = yield select(isCreateMemorizedRulesEnabled);
  const isSharedUser = yield select(isEntityShareUser);
  if (isEnabled && !isSharedUser) {
    const transactions = Array.isArray(action.payload) ? action.payload : [action.payload];
    yield call(upsertMemorizedPayeeRule, transactions, state);
  }
}

// This action watcher creates and updates the memorized payee 
export function* transactionAddAndUpdateActionWatcher() {
  yield takeLatest([
    transactionsActions.batchTransactionsSuccess,
    transactionsActions.createTransactionSuccess,
    transactionsActions.updateTransactionSuccess,
  ], updateMemorizedPayee);
}

// ====================================================
// EXPORTS

export default [
  updatePayeeListActionWatcher,
  transactionChangeActionWatcher,
  transactionAddAndUpdateActionWatcher,
];
